// The Academy's stand-in for a video thumbnail: a soft gradient tile in the
// course's hue with its heroicon large and faint, an optional live progress
// ring in a corner, and an optional glyph (play for a lesson with a scene,
// cursor for one with a live walkthrough). Three sizes: `card` (16:9 top of a
// course card), `hero` (course page header), `row` (lesson list rows).
import { CursorArrowRaysIcon, PlayIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import ProgressRing from "../../components/common/ProgressRing";
import { Course } from "../catalog/types";
import { CourseIcon } from "./courseIcons";

// Literal class strings so Tailwind's JIT sees them. The blue scale is the
// app's custom one (no 950), so its dark twins stay on 900.
const HUES: Record<Course["icon"], { tile: string; icon: string }> = {
  rocket: { tile: "from-blue-100 to-blue-50 dark:from-blue-900/60 dark:to-blue-900/20", icon: "text-blue-500" },
  microphone: { tile: "from-violet-100 to-violet-50 dark:from-violet-950/70 dark:to-violet-950/20", icon: "text-violet-500" },
  pencil: { tile: "from-emerald-100 to-emerald-50 dark:from-emerald-950/70 dark:to-emerald-950/20", icon: "text-emerald-500" },
  sparkles: { tile: "from-amber-100 to-amber-50 dark:from-amber-950/70 dark:to-amber-950/20", icon: "text-amber-500" },
  "document-duplicate": { tile: "from-sky-100 to-sky-50 dark:from-sky-950/70 dark:to-sky-950/20", icon: "text-sky-500" },
  folder: { tile: "from-orange-100 to-orange-50 dark:from-orange-950/70 dark:to-orange-950/20", icon: "text-orange-500" },
  "magnifying-glass": { tile: "from-indigo-100 to-indigo-50 dark:from-indigo-950/70 dark:to-indigo-950/20", icon: "text-indigo-500" },
  "video-camera": { tile: "from-rose-100 to-rose-50 dark:from-rose-950/70 dark:to-rose-950/20", icon: "text-rose-500" },
  "shield-check": { tile: "from-teal-100 to-teal-50 dark:from-teal-950/70 dark:to-teal-950/20", icon: "text-teal-500" },
  cog: { tile: "from-slate-200 to-slate-100 dark:from-slate-800/70 dark:to-slate-800/20", icon: "text-slate-500" },
  "building-office": { tile: "from-cyan-100 to-cyan-50 dark:from-cyan-950/70 dark:to-cyan-950/20", icon: "text-cyan-500" },
};

export const courseHue = (icon: Course["icon"]) => HUES[icon] ?? HUES.rocket;

export type ThumbnailGlyph = "watch" | "walkthrough";

const SIZES = {
  card: { box: "aspect-[16/9] w-full rounded-t-lg", icon: "h-16 w-16", glyph: "h-9 w-9", glyphIcon: "h-4 w-4" },
  hero: { box: "h-28 w-44 rounded-lg", icon: "h-14 w-14", glyph: "h-8 w-8", glyphIcon: "h-3.5 w-3.5" },
  row: { box: "h-12 w-20 rounded-md", icon: "h-7 w-7", glyph: "h-5 w-5", glyphIcon: "h-2.5 w-2.5" },
} as const;

const CourseThumbnail: React.FC<{
  course: Course;
  size?: keyof typeof SIZES;
  /** 1–100 shows a ring in the corner (card/hero only). */
  progressPct?: number;
  glyph?: ThumbnailGlyph | null;
  className?: string;
}> = ({ course, size = "card", progressPct, glyph, className }) => {
  const hue = courseHue(course.icon);
  const s = SIZES[size];
  const showRing = size !== "row" && typeof progressPct === "number" && progressPct > 0;
  return (
    <div className={cn("relative shrink-0 overflow-hidden bg-gradient-to-br border border-base-300/60", hue.tile, s.box, className)} aria-hidden>
      <div className="absolute inset-0 flex items-center justify-center">
        <CourseIcon icon={course.icon} className={cn(s.icon, hue.icon, "opacity-30 dark:opacity-40")} />
      </div>
      {glyph && (
        <span className={cn("absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center rounded-full bg-blue-500 text-white shadow-md", s.glyph)}>
          {glyph === "watch" ? <PlayIcon className={cn(s.glyphIcon, "ml-px")} /> : <CursorArrowRaysIcon className={s.glyphIcon} />}
        </span>
      )}
      {showRing && (
        <div className="absolute right-2 top-2 rounded-full bg-base-100/90 p-0.5">
          <ProgressRing value={progressPct!} size="xs" />
        </div>
      )}
    </div>
  );
};

export default CourseThumbnail;
